import { ClientEvents } from 'common/events/ClientEvents';
import { Slugs } from 'common/urls/Slugs';
import { isEmpty, noop, size } from 'lodash';
import React, { useCallback, useState } from 'react';
import { ConsumerGroupOrderFieldsFragment, useReorderMutation } from 'src/gqlReactTypings.generated.d';
import { ButtonColors } from 'src/shared/design-system/Buttons';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Notifications } from 'src/shared/Notifications';
import { Routes } from 'src/shared/Routes';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useDispatch } from 'src/store';
import { forceCartRefetch } from 'src/store/cart/actions';
import styled from 'styled-components';
import { Colors } from 'web-common/src/shared/styles';
import { OrdersListRowButton } from './styles';

interface Props {
  groupOrder: ConsumerGroupOrderFieldsFragment;
  primary?: boolean;
}

const ReorderButtonStyled = styled(OrdersListRowButton).attrs((_props: { primary?: boolean }) => ({
  color: ButtonColors.WHITE,
}))`
  color: ${(props) => (props.primary ? Colors.WHITE : Colors.RASPBERRY)};
  border: 2px solid ${(props) => (props.primary ? Colors.RASPBERRY : Colors.RASPBERRY)};
  background-color: ${(props) => (props.primary ? Colors.RASPBERRY : Colors.WHITE)};
`;

export const ReorderButton: React.FC<Props> = ({ groupOrder: { orders }, primary = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reorderMutation] = useReorderMutation();
  const history = useHistory();
  const dispatch = useDispatch();
  const tracker = useTracker();

  const { id, shefId } = orders[0];

  const doReorder = useCallback(async () => {
    setIsLoading(true);

    const { errors } = await reorderMutation({
      variables: {
        orderId: id,
      },
    });

    if (errors) {
      Notifications.error(errors[0].message);
      console.error('Failed to duplicate to cart. Backend responded with error.');
      setIsLoading(false);
      return;
    }

    // Get the newly added cart items from the server:
    dispatch(forceCartRefetch());
    tracker.track(ClientEvents.REORDER_BUTTON_CLICK, { shefId, orderId: id });

    const redirectPath = getSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, [{ slug: Slugs.ID, value: shefId }], {});
    history.push(redirectPath);
  }, [id, reorderMutation, shefId, history, dispatch, tracker]);

  if (size(orders) > 1 || isEmpty(orders[0].publicShef.availability)) {
    return null;
  }

  return (
    <ReorderButtonStyled onClick={isLoading ? noop : doReorder} primary={primary}>
      {isLoading ? `Loading` : `Reorder`}
    </ReorderButtonStyled>
  );
};
